/**
 * Frame3_3_1 - Detailed Distribution Plan
 *
 * Purpose: Detailed beneficiary assignments and distribution percentages
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { useSettlementSession } from '../../hooks/useSettlementSession';

export default function Frame3_3_1() {
  const navigate = useNavigate();
  const { estateId } = useParams();
  const { loadPlannerData } = useSettlementSession(estateId || null);
  const [distribution, setDistribution] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!estateId) {
        setLoading(false);
        return;
      }

      try {
        const distributionData = await loadPlannerData('frame-246');
        setDistribution(distributionData);
      } catch (error) {
        console.error('Failed to load distribution data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [estateId, loadPlannerData]);

  const handleFinish = () => {
    navigate(estateId ? `/sframe-111/${estateId}` : '/sframe-111');
  };

  const handleBack = () => {
    navigate(estateId ? `/frame-3-3/${estateId}` : '/frame-3-3');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading distribution plan...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Distribution Details</h1>
          <p className="text-gray-600">
            Specific beneficiary assignments and allocation percentages
          </p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Beneficiary Allocations</h2>
          {distribution && Object.keys(distribution).length > 0 ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-green-900 mb-1">
                      Distribution Plan Complete
                    </h3>
                    <p className="text-sm text-green-800">
                      The deceased created a complete distribution plan with beneficiary
                      assignments and percentages. Full details will be available during the
                      distribution phase of settlement.
                    </p>
                  </div>
                </div>
              </div>
              <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-4">
                <p className="font-medium mb-2">Access to Full Distribution Plan:</p>
                <p>
                  As you progress through settlement, you'll have access to all distribution
                  details when you reach the "Distribute Assets" phase. This ensures you work
                  through necessary legal and financial steps before executing the distribution.
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800">
                No distribution plan was documented. You may need to follow intestacy laws or
                consult with legal counsel.
              </p>
            </div>
          )}
        </div>

        <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-cyan-900 mb-3">Ready to Begin Settlement?</h2>
          <p className="text-cyan-800 mb-4">
            You've reviewed the estate plan overview. You're now ready to begin the settlement
            workflow, starting with immediate actions after death.
          </p>
          <p className="text-sm text-cyan-700">
            The settlement process will guide you step-by-step through securing assets, legal
            filings, notifications, debt settlement, and final distribution.
          </p>
        </div>

        <div className="flex justify-between items-center">
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button onClick={handleFinish} className="bg-cyan-600 hover:bg-cyan-700">
            Begin Settlement Workflow
            <CheckCircle className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
